import { Check, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import type { ReviewStatus } from '@/types'

/**
 * The paired verdict control. Used identically on Programs, Quotes, Media and
 * Partner Facts — a reviewer learns the gesture once (docs/03-page-specs.md §4).
 *
 * The current verdict is shown as a pressed state rather than by hiding the
 * other button: a reviewer must always be able to change their mind, and a
 * disappearing control reads as a bug.
 */
export function ReviewActions({
  status,
  onApprove,
  onReject,
  size = 'md',
  className,
}: {
  status: ReviewStatus
  onApprove: () => void
  onReject: () => void
  size?: 'sm' | 'md'
  className?: string
}) {
  const dims = size === 'sm' ? 'h-6 px-2 text-[11px]' : 'h-7 px-2.5 text-[12px]'

  return (
    <div className={cn('inline-flex gap-1.5', className)} role="group" aria-label="Verdict">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onApprove()
        }}
        aria-pressed={status === 'approved'}
        className={cn(
          'inline-flex items-center gap-1 rounded-[4px] border font-head font-semibold transition-colors',
          dims,
          status === 'approved'
            ? 'border-affirm bg-affirm text-white'
            : 'border-rule-strong text-affirm hover:bg-affirm-50',
        )}
      >
        <Check size={12} aria-hidden />
        Approve
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onReject()
        }}
        aria-pressed={status === 'rejected'}
        className={cn(
          'inline-flex items-center gap-1 rounded-[4px] border font-head font-semibold transition-colors',
          dims,
          status === 'rejected'
            ? 'border-brand-red bg-brand-red text-white'
            : 'border-rule-strong text-brand-red-700 hover:bg-brand-red-50',
        )}
      >
        <X size={12} aria-hidden />
        Reject
      </button>
    </div>
  )
}
